import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DENIED_CONSENT,
  GRANTED_CONSENT,
  applyGlobalPrivacyControl,
  hasGlobalPrivacyControl,
  hasOptionalConsent,
  isConsentablePath,
  serializeConsentCookie,
  type CookieConsent,
} from '~/lib/cookie-consent';
import { initializeConsentMode, isGtmLoaded, loadGtm, updateConsentMode } from '~/lib/gtm';

/**
 * Cookie consent, ported from the Academy.
 *
 * The decision arrives from the server (parsed out of the Cookie header in the
 * root loader), so a returning visitor never sees the banner flash before it is
 * dismissed again — the first paint already knows.
 */

type CookieConsentContextValue = {
  consent: CookieConsent | null;
  /** True once any optional category is granted. */
  canTrack: boolean;
  openSettings: (trigger?: HTMLElement | null) => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({
  initialConsent,
  gtmId,
  children,
}: {
  initialConsent: CookieConsent | null;
  /** Null when the tag manager is not configured for this request. */
  gtmId: string | null;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('cookies');
  const location = useLocation();
  const [consent, setConsent] = useState<CookieConsent | null>(initialConsent);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<CookieConsent>(initialConsent ?? DENIED_CONSENT);
  const triggerRef = useRef<HTMLElement | null>(null);

  /**
   * GPC via an external store rather than state-in-an-effect: the server has no
   * navigator to ask, and the signal does not change during a page's life, so
   * there is nothing to subscribe to — only a server snapshot (unknown, so
   * false) and a client one. React re-renders once on hydration with the real
   * value, which is exactly the intent.
   */
  const gpc = useSyncExternalStore(
    () => () => {},
    () => hasGlobalPrivacyControl(window.navigator),
    () => false
  );

  const effective = consent ? applyGlobalPrivacyControl(consent, gpc) : null;
  const askable = isConsentablePath(location.pathname);

  // Denied-by-default before anything else gets the chance to run.
  useEffect(() => initializeConsentMode(), []);

  /**
   * A stored "advertising: true" from before the browser started sending GPC
   * must not keep applying. `effective` already masks it for this render; this
   * narrows the stored cookie too, so the tighter choice is what persists. No
   * state update — `effective` is derived, so there is nothing to sync.
   */
  useEffect(() => {
    if (!gpc || !consent?.advertising) return;
    document.cookie = serializeConsentCookie(
      applyGlobalPrivacyControl(consent, true),
      window.location.protocol === 'https:'
    );
  }, [gpc, consent]);

  useEffect(() => {
    if (!effective || !askable) return;
    updateConsentMode(effective);
    if (hasOptionalConsent(effective)) loadGtm(gtmId);
  }, [effective, askable, gtmId]);

  const save = useCallback(
    (next: CookieConsent) => {
      const applied = applyGlobalPrivacyControl(next, hasGlobalPrivacyControl(window.navigator));
      // Withdrawing consent cannot un-run a container that is already on the
      // page; only a reload actually removes it.
      const withdrawn = Boolean(
        consent &&
        isGtmLoaded() &&
        ((consent.analytics && !applied.analytics) || (consent.advertising && !applied.advertising))
      );
      document.cookie = serializeConsentCookie(applied, window.location.protocol === 'https:');
      setConsent(applied);
      setDraft(applied);
      setSettingsOpen(false);
      updateConsentMode(applied);
      if (withdrawn) window.location.reload();
    },
    [consent]
  );

  const openSettings = useCallback(
    (trigger?: HTMLElement | null) => {
      triggerRef.current = trigger ?? null;
      setDraft(applyGlobalPrivacyControl(consent ?? DENIED_CONSENT, hasGlobalPrivacyControl(window.navigator)));
      setSettingsOpen(true);
    },
    [consent]
  );

  const value = useMemo<CookieConsentContextValue>(
    () => ({ consent: effective, canTrack: hasOptionalConsent(effective), openSettings }),
    [effective, openSettings]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}

      {consent === null && askable && (
        <section
          aria-label={t('banner.label')}
          className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-[760px] flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-2xl md:p-6"
        >
          <div className="flex flex-col gap-2">
            <h2 className="font-display text-lg leading-6 font-bold text-foreground md:text-xl">{t('banner.title')}</h2>
            <p className="text-[15px] leading-6 text-foreground-soft">
              {t('banner.body')}{' '}
              <Link
                to="/privacy-policy"
                className="rounded-sm underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('banner.privacyLink')}
              </Link>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="min-h-11 flex-1" onClick={() => save(GRANTED_CONSENT)}>
              {t('actions.acceptAll')}
            </Button>
            <Button className="min-h-11 flex-1" variant="outline" onClick={() => save(DENIED_CONSENT)}>
              {t('actions.rejectOptional')}
            </Button>
            <Button
              className="min-h-11 sm:flex-1"
              variant="ghost"
              onClick={(event) => openSettings(event.currentTarget)}
            >
              {t('actions.manage')}
            </Button>
          </div>
        </section>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent
          className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
          aria-describedby="cookie-settings-description"
          onCloseAutoFocus={(event) => {
            // Send focus back to whatever opened the dialog, which for the footer
            // link is well away from where the dialog was.
            const trigger = triggerRef.current;
            if (!trigger) return;
            event.preventDefault();
            trigger.focus();
            triggerRef.current = null;
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('settings.title')}</DialogTitle>
            <DialogDescription id="cookie-settings-description">
              {t('settings.description')}{' '}
              <Link
                to="/privacy-policy"
                onClick={() => setSettingsOpen(false)}
                className="rounded-sm underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('banner.privacyLink')}
              </Link>
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <PreferenceRow
              id="cookies-essential"
              title={t('categories.essential.title')}
              description={t('categories.essential.body')}
              checked
              disabled
            />
            <PreferenceRow
              id="cookies-analytics"
              title={t('categories.analytics.title')}
              description={t('categories.analytics.body')}
              checked={draft.analytics}
              onCheckedChange={(checked) => setDraft((current) => ({ ...current, analytics: checked === true }))}
            />
            <PreferenceRow
              id="cookies-advertising"
              title={t('categories.advertising.title')}
              description={gpc ? t('categories.advertising.gpc') : t('categories.advertising.body')}
              checked={draft.advertising}
              disabled={gpc}
              onCheckedChange={(checked) => setDraft((current) => ({ ...current, advertising: checked === true }))}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row">
            <Button className="min-h-11 flex-1" onClick={() => save(draft)}>
              {t('actions.save')}
            </Button>
            <Button className="min-h-11 flex-1" variant="outline" onClick={() => save(DENIED_CONSENT)}>
              {t('actions.rejectOptional')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CookieConsentContext.Provider>
  );
}

function PreferenceRow({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 has-[:disabled]:cursor-default"
    >
      <Checkbox
        id={id}
        aria-describedby={`${id}-description`}
        className="mt-0.5 size-5"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
      <span>
        <span className="block text-[15px] font-semibold text-foreground">{title}</span>
        <span id={`${id}-description`} className="mt-1 block text-[13px] leading-5 text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * Returns null outside the provider rather than throwing, so the footer's
 * preferences link can render on pages that never mount it (/admin, /review).
 */
export function useCookieConsent(): CookieConsentContextValue | null {
  return useContext(CookieConsentContext);
}
